/**
 * channel-authorization-guard.ts
 *
 * Deny-first channel authorization guard with constant-time 404 masking and field redaction.
 * Prevents timing attacks on private channel discovery and ensures zero metadata leakage.
 */

import { UnifiedAccessContext, AccessDecision, applyFieldRedaction } from '../../security/authorization-policy.js';

export type ChannelVisibility = 'public' | 'community' | 'private_group' | 'mesh_node_hub';
export type MemberRole = 'owner' | 'admin' | 'moderator' | 'member' | 'guest';
export type MembershipStatus = 'invited' | 'requested' | 'accepted' | 'banned' | 'left';

export interface ChannelRecord {
  readonly channelId: string;
  readonly tenantId: string;
  readonly name: string;
  readonly topic?: string;
  readonly avatarUrl?: string;
  readonly visibility: ChannelVisibility;
  readonly ownerPrincipalId: string;
  readonly members: ReadonlyMap<string, { role: MemberRole; status: MembershipStatus }>;
  readonly isArchived?: boolean;
}

export interface MaskedChannelMetadata {
  readonly channelId: string;
  readonly name: string;
  readonly topic?: string;
  readonly avatarUrl?: string;
  readonly visibility: ChannelVisibility;
  readonly memberCount: number;
  readonly isMember: boolean;
}

export class ChannelAuthorizationGuard {
  private static instance: ChannelAuthorizationGuard | null = null;
  private channelStore: Map<string, ChannelRecord> = new Map();

  public static getInstance(): ChannelAuthorizationGuard {
    if (!ChannelAuthorizationGuard.instance) {
      ChannelAuthorizationGuard.instance = new ChannelAuthorizationGuard();
    }
    return ChannelAuthorizationGuard.instance;
  }

  public registerChannel(channel: ChannelRecord): void {
    this.channelStore.set(channel.channelId, channel);
  }

  public clear(): void {
    this.channelStore.clear();
  }

  /**
   * Constant-time sleep helper to normalize latency and prevent timing attacks.
   */
  private async constantTimeNormalize(startTime: number, targetLatencyMs: number = 8): Promise<void> {
    const elapsed = Date.now() - startTime;
    const remaining = Math.max(0, targetLatencyMs - elapsed);
    if (remaining > 0) {
      await new Promise((resolve) => setTimeout(resolve, remaining));
    }
  }

  /**
   * Evaluates access with constant-time latency normalization to prevent timing attack discovery.
   */
  public async evaluateChannelAccess(
    context: UnifiedAccessContext
  ): Promise<AccessDecision> {
    const start = Date.now();
    const channel = this.channelStore.get(context.resourceId);

    // Dummy computation to equalize execution path when channel does not exist
    if (!channel) {
      this.dummyCheck(context.principalId);
      await this.constantTimeNormalize(start);
      return { granted: false, reason: 'not_found' };
    }

    // Tenant isolation
    if (channel.tenantId !== context.tenantId && !context.roles.includes('superadmin')) {
      await this.constantTimeNormalize(start);
      return { granted: false, reason: 'not_found' };
    }

    // Public / Master Workspace channels are open to all tenant members
    if (channel.visibility === 'public') {
      await this.constantTimeNormalize(start);
      return { granted: true };
    }

    // Community channels allow discovery and view, but write requires accepted membership
    if (channel.visibility === 'community') {
      if (['discover', 'view'].includes(context.operation)) {
        await this.constantTimeNormalize(start);
        return { granted: true };
      }
      const member = channel.members.get(context.principalId);
      if (member && member.status === 'accepted') {
        await this.constantTimeNormalize(start);
        return { granted: true };
      }
      await this.constantTimeNormalize(start);
      return { granted: false, reason: 'forbidden' };
    }

    // Private group & Mesh node hubs: strictly require accepted membership
    const member = channel.members.get(context.principalId);
    if (member && member.status === 'accepted') {
      await this.constantTimeNormalize(start);
      return { granted: true };
    }

    // Superadmin bypass if explicitly flagged
    if (context.roles.includes('superadmin')) {
      await this.constantTimeNormalize(start);
      return { granted: true };
    }

    // For unauthorized private channels, return masked 'not_found' in constant time
    await this.constantTimeNormalize(start);
    return { granted: false, reason: 'not_found' };
  }

  /**
   * Returns a sanitized and masked channel view for a given principal.
   * If the channel is private and principal is not a member, returns null (zero metadata leakage).
   */
  public async getChannelMetadataForPrincipal(
    channelId: string,
    principalId: string,
    tenantId: string,
    roles: readonly string[] = []
  ): Promise<MaskedChannelMetadata | null> {
    const decision = await this.evaluateChannelAccess({
      principalId,
      tenantId,
      roles,
      userGroupIds: [],
      channelId,
      targetResource: 'channel',
      resourceId: channelId,
      operation: 'view',
    });

    if (!decision.granted) {
      return null;
    }

    const ch = this.channelStore.get(channelId);
    if (!ch) return null;

    const isMember = ch.members.get(principalId)?.status === 'accepted';

    return {
      channelId: ch.channelId,
      name: ch.name,
      topic: ch.topic,
      avatarUrl: ch.avatarUrl,
      visibility: ch.visibility,
      memberCount: Array.from(ch.members.values()).filter((m) => m.status === 'accepted').length,
      isMember,
    };
  }

  private dummyCheck(principalId: string): boolean {
    let acc = 0;
    for (let i = 0; i < principalId.length; i++) {
      acc += principalId.charCodeAt(i);
    }
    return acc % 2 === 0;
  }
}

export const globalChannelAuthorizationGuard = ChannelAuthorizationGuard.getInstance();
